import { useEffect, useCallback, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useUserPreferences } from './useUserPreferences';
import { useSystemSetting } from './useSystemSettings';
import { usePermissions } from './usePermissions';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

const WARNING_TIME = 60 * 1000; // 60 segundos de warning antes do logout
const LAST_ACTIVITY_KEY = 'arete:last_activity';

export const useSessionTimeout = () => {
  const { signOut, user } = useAuth();
  const { data: preferences } = useUserPreferences();
  // Only query system settings when user is authenticated to avoid 406 errors
  const { data: globalSetting } = useSystemSetting('global_session_timeout_minutes', { enabled: !!user });
  const { isAdmin, isSupervisor } = usePermissions();
  const { toast } = useToast();

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const timeoutMsRef = useRef<number>(30 * 60 * 1000);
  const showWarningRef = useRef<boolean>(false);
  const [showWarning, setShowWarning] = useState(false);

  // Recalcula o timeout efetivo sempre que preferências/papel mudam.
  // Mantém em ref para que callbacks/listeners sejam estáveis.
  useEffect(() => {
    const userTimeout = preferences?.session_timeout_minutes;
    const globalTimeout = globalSetting?.value ? parseInt(globalSetting.value) : 30;
    let timeoutMinutes = userTimeout || globalTimeout;
    if (!isAdmin() && !isSupervisor()) {
      timeoutMinutes = Math.min(timeoutMinutes, 60);
    } else {
      timeoutMinutes = Math.min(timeoutMinutes, 120);
    }
    timeoutMsRef.current = Math.max(timeoutMinutes, 1) * 60 * 1000;
  }, [preferences, globalSetting, isAdmin, isSupervisor]);

  const setShowWarningSafe = useCallback((v: boolean) => {
    showWarningRef.current = v;
    setShowWarning(v);
  }, []);

  const clearTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningRef.current) {
      clearTimeout(warningRef.current);
      warningRef.current = null;
    }
  }, []);

  const handleTimeout = useCallback(async () => {
    const now = Date.now();
    let stored: number | null = null;
    try {
      const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
      stored = raw ? parseInt(raw, 10) : null;
    } catch {}

    if (stored && now - stored < timeoutMsRef.current) {
      const remaining = timeoutMsRef.current - (now - stored);
      clearTimers();
      setShowWarningSafe(false);
      lastActivityRef.current = stored;
      warningRef.current = setTimeout(() => setShowWarningSafe(true), Math.max(remaining - WARNING_TIME, 0));
      timeoutRef.current = setTimeout(() => { void handleTimeout(); }, remaining);
      return;
    }

    clearTimers();
    setShowWarningSafe(false);
    try { localStorage.removeItem(LAST_ACTIVITY_KEY); } catch {}
    toast({
      title: 'Sessão expirada',
      description: 'Sua sessão expirou por inatividade.',
      variant: 'destructive',
    });
    await signOut({ scope: 'local' });
  }, [signOut, toast, clearTimers, setShowWarningSafe]);

  const persistActivity = useCallback((ts: number) => {
    try { localStorage.setItem(LAST_ACTIVITY_KEY, String(ts)); } catch {}
  }, []);

  // Reseta o timer. Se `remainingMs` for fornecido, usa esse valor (sem regravar atividade).
  const resetTimeout = useCallback((remainingMs?: number) => {
    clearTimers();
    const timeout = timeoutMsRef.current;
    const effective = typeof remainingMs === 'number' ? Math.max(remainingMs, 0) : timeout;

    if (typeof remainingMs !== 'number') {
      const now = Date.now();
      lastActivityRef.current = now;
      persistActivity(now);
    }
    setShowWarningSafe(false);

    const warningTime = Math.max(effective - WARNING_TIME, 0);
    warningRef.current = setTimeout(() => setShowWarningSafe(true), warningTime);
    timeoutRef.current = setTimeout(() => { void handleTimeout(); }, effective);
  }, [clearTimers, persistActivity, setShowWarningSafe, handleTimeout]);

  const extendSession = useCallback(async () => {
    setShowWarningSafe(false);
    try {
      const { error } = await supabase.auth.refreshSession();
      if (error) {
        logger.error('Falha ao renovar sessão:', error);
        try { localStorage.removeItem(LAST_ACTIVITY_KEY); } catch {}
        await supabase.auth.signOut({ scope: 'local' });
        toast({
          title: 'Sessão não pôde ser renovada',
          description: 'Faça login novamente para continuar.',
          variant: 'destructive',
        });
        return;
      }
      logger.info('Sessão renovada manualmente pelo usuário');
    } catch (e) {
      logger.error('Erro inesperado ao renovar sessão:', e);
      toast({
        title: 'Sessão não pôde ser renovada',
        description: 'Faça login novamente para continuar.',
        variant: 'destructive',
      });
      try { localStorage.removeItem(LAST_ACTIVITY_KEY); } catch {}
      await supabase.auth.signOut({ scope: 'local' });
      return;
    }
    resetTimeout();
  }, [resetTimeout, toast, setShowWarningSafe]);

  // Registra atividade (throttled a 60s). Não reseta durante o warning.
  const recordActivity = useCallback(() => {
    if (showWarningRef.current) return;
    const now = Date.now();
    if (now - lastActivityRef.current > 60 * 1000) {
      resetTimeout();
    }
  }, [resetTimeout]);

  const checkStoredActivity = useCallback(() => {
    if (showWarningRef.current) return;
    const timeout = timeoutMsRef.current;
    let stored: number | null = null;
    try {
      const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
      stored = raw ? parseInt(raw, 10) : null;
    } catch {}

    const now = Date.now();
    if (stored && now - stored > timeout) {
      void handleTimeout();
      return;
    }

    const remaining = stored ? timeout - (now - stored) : timeout;
    lastActivityRef.current = stored ?? now;
    if (!stored) persistActivity(now);
    resetTimeout(remaining);
  }, [handleTimeout, resetTimeout, persistActivity]);

  // Listener único por sessão de usuário. Não depende de preferências para evitar
  // recriações constantes que reiniciariam os timers.
  useEffect(() => {
    if (!user) {
      clearTimers();
      return;
    }

    const events = ['mousedown', 'pointerdown', 'pointermove', 'keydown', 'keypress', 'input', 'scroll', 'wheel', 'touchstart', 'click'];
    events.forEach((event) => {
      document.addEventListener(event, recordActivity, { passive: true });
    });

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkStoredActivity();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    checkStoredActivity();

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, recordActivity);
      });
      document.removeEventListener('visibilitychange', onVisibility);
      clearTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  return {
    resetTimeout,
    recordActivity,
    extendSession,
    showWarning,
    handleTimeout,
    getSessionTimeout: () => timeoutMsRef.current / (60 * 1000),
  };
};
